import { z } from 'zod';
import { LIMITS } from '../limits.js';

/**
 * One file attached to a message.
 *
 * Uploaded via the presigned-S3 flow (POST /api/uploads/sign → PUT to S3 →
 * POST /api/uploads/complete → server returns this shape, client sends it
 * back inside `sendMessage({ attachments: [...] })`).
 *
 * `id` is the storage-key the server minted; clients echo it back so the
 * server can verify the upload actually happened.
 */
export const attachmentSchema = z.object({
  id: z.string().min(1).max(128),
  name: z.string().min(1).max(256),
  size: z.number().int().min(0).max(LIMITS.attachment.sizeBytesPremium),
  mime: z.string().min(1).max(128),
  url: z.string().url(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  durationMs: z.number().int().positive().optional(),
  /** Voice-note amplitude envelope: 0..1 levels sampled while recording (≤64
   *  buckets), so players draw the REAL waveform instead of a pseudo one. */
  waveform: z.array(z.number().min(0).max(1)).max(64).optional(),
  /** Force a download-chip render even for image/* (Telegram "send as file"). */
  asFile: z.boolean().optional(),
});

export type Attachment = z.infer<typeof attachmentSchema>;

/**
 * Request body for "give me a presigned URL to upload X".
 */
/** Strip MediaRecorder's codec parameter so `audio/webm;codecs=opus`
 *  matches the allowlisted base `audio/webm` (and likewise for ogg/mp4). */
export function baseMimeType(mime: string): string {
  return mime.split(';')[0]!.trim().toLowerCase();
}

export const presignUploadSchema = z.object({
  name: z.string().min(1).max(256),
  size: z.number().int().min(0).max(LIMITS.attachment.sizeBytesPremium),
  mime: z.string().min(1).max(128).refine(
    (m) => LIMITS.attachment.mimeAllowlist.includes(baseMimeType(m)),
    { message: 'mime_not_allowed' },
  ),
});
export type PresignUploadInput = z.infer<typeof presignUploadSchema>;
